"use client";

import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { useMediaPlayer } from '@/contexts/media-player-context';
import { endCall, subscribeCall, type CallDoc } from '@/services/calls';
import {
  createPeerConnection,
  closePeerConnection,
  getMediaStream,
  enumerateDevices,
  stopMediaStream,
} from '@/services/webrtc';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import {
  Phone,
  PhoneOff,
  Mic,
  MicOff,
  Video,
  VideoOff,
  Volume2,
  VolumeX,
  FlipHorizontal,
  Maximize,
  Minimize,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface ActiveCallUIProps {
  callId: string;
  onCallEnded?: () => void;
}

// ── Format mm:ss or h:mm:ss ────────────────────────────────────────────────
function formatDuration(seconds: number) {
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  if (hrs > 0) {
    return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
}

// ── Round control button ───────────────────────────────────────────────────
function ControlBtn({
  onClick,
  active = false,
  danger = false,
  large = false,
  label,
  children,
  disabled = false,
}: {
  onClick: () => void;
  active?: boolean;
  danger?: boolean;
  large?: boolean;
  label: string;
  children: React.ReactNode;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className={cn(
        'flex flex-col items-center gap-1.5 select-none',
        disabled && 'opacity-40 cursor-not-allowed',
      )}
    >
      <span
        className={cn(
          'flex items-center justify-center rounded-full transition-colors',
          large ? 'h-16 w-16' : 'h-14 w-14',
          danger
            ? 'bg-red-600 hover:bg-red-500 text-white'
            : active
            ? 'bg-white/20 hover:bg-white/30 text-white'
            : 'bg-white/10 hover:bg-white/20 text-white/80',
        )}
      >
        {children}
      </span>
      <span className="text-[10px] text-white/60 font-medium">{label}</span>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// Main component
// ═══════════════════════════════════════════════════════════════════════════════

export function ActiveCallUI({ callId, onCallEnded }: ActiveCallUIProps) {
  const { user } = useAuth();
  const [call, setCall] = useState<CallDoc | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOn, setIsCameraOn] = useState(true);
  const [isSpeakerOn, setIsSpeakerOn] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentVideoDeviceIdx, setCurrentVideoDeviceIdx] = useState(0);
  const [webRTCReady, setWebRTCReady] = useState(false);

  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  // Separate audio element for audio-only calls (no <video> element rendered)
  const remoteAudioRef = useRef<HTMLAudioElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const pcRef = useRef<RTCPeerConnection | null>(null);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamRef = useRef<MediaStream | null>(null);
  const durationTimerRef = useRef<NodeJS.Timeout | null>(null);
  const micVuCleanupRef = useRef<(() => void) | null>(null);

  const { connectMicStream } = useMediaPlayer();

  // ── Subscribe to call doc ──────────────────────────────────────────────────
  useEffect(() => {
    const unsub = subscribeCall(callId, (callDoc) => {
      setCall(callDoc);
      if (!callDoc || callDoc.status === 'ended' || callDoc.status === 'declined' || callDoc.status === 'missed') {
        onCallEnded?.();
      }
    });
    return () => unsub();
  }, [callId, onCallEnded]);

  // ── Enumerate video devices ────────────────────────────────────────────────
  useEffect(() => {
    enumerateDevices().then((devices) => {
      setVideoDevices(devices.filter((d) => d.kind === 'videoinput'));
    });
  }, []);

  // ── Initialize WebRTC when call becomes active ─────────────────────────────
  useEffect(() => {
    if (!call || call.status !== 'active' || !user?.uid || webRTCReady) return;
    let cancelled = false;

    const init = async () => {
      try {
        const constraints = {
          audio: true,
          video: call.type === 'video',
        };

        const localStream = await getMediaStream(constraints);
        if (cancelled) { stopMediaStream(localStream); return; }

        localStreamRef.current = localStream;

        // Route mic to global VU strip
        micVuCleanupRef.current?.();
        micVuCleanupRef.current = connectMicStream(localStream);

        if (localVideoRef.current && call.type === 'video') {
          localVideoRef.current.srcObject = localStream;
        }

        const isInitiator = call.callerId === user.uid;
        const { pc, remoteStream } = await createPeerConnection(callId, isInitiator, localStream);
        if (cancelled) { closePeerConnection(pc); stopMediaStream(localStream); return; }

        pcRef.current = pc;
        remoteStreamRef.current = remoteStream;

        // Connect remote stream to the appropriate output element:
        // audio calls → remoteAudioRef, video calls → remoteVideoRef
        const remoteMediaEl = call.type === 'video'
          ? remoteVideoRef.current
          : remoteAudioRef.current;
        if (remoteMediaEl) {
          remoteMediaEl.srcObject = remoteStream;
        }

        // When remote tracks arrive, ensure the element plays (autoplay policy).
        // Use addtrack on the stream — fires even if ontrack already fired.
        const tryPlay = () => {
          if (remoteMediaEl && remoteStream.getTracks().length > 0) {
            remoteMediaEl.play().catch(() => {});
          }
        };
        remoteStream.addEventListener('addtrack', tryPlay);
        // Also check immediately in case tracks already arrived (race condition)
        tryPlay();

        setWebRTCReady(true);

        // Start duration timer from answeredAt
        const answeredMs = call.answeredAt?.toMillis?.() ?? Date.now();
        durationTimerRef.current = setInterval(() => {
          setCallDuration(Math.floor((Date.now() - answeredMs) / 1000));
        }, 1000);
      } catch (err) {
        console.error('[ActiveCallUI] WebRTC init error:', err);
      }
    };

    init();

    return () => {
      cancelled = true;
      if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.status, call?.type, callId, user?.uid]);

  // ── Controls ───────────────────────────────────────────────────────────────

  const handleMuteToggle = useCallback(() => {
    if (!localStreamRef.current) return;
    const next = !isMuted;
    localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = !next; });
    setIsMuted(next);
  }, [isMuted]);

  const handleCameraToggle = useCallback(() => {
    if (!localStreamRef.current) return;
    const next = !isCameraOn;
    localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = next; });
    setIsCameraOn(next);
  }, [isCameraOn]);

  const handleSpeakerToggle = useCallback(() => {
    // muted = true means speaker OFF (we're turning it off when isSpeakerOn is currently true)
    const shouldMute = isSpeakerOn;
    if (remoteVideoRef.current) remoteVideoRef.current.muted = shouldMute;
    if (remoteAudioRef.current) remoteAudioRef.current.muted = shouldMute;
    setIsSpeakerOn(!isSpeakerOn);
  }, [isSpeakerOn]);

  const handleFlipCamera = useCallback(async () => {
    if (videoDevices.length < 2 || !localStreamRef.current) return;
    const nextIdx = (currentVideoDeviceIdx + 1) % videoDevices.length;
    const nextDevice = videoDevices[nextIdx];

    // Stop current video tracks
    localStreamRef.current.getVideoTracks().forEach((t) => t.stop());

    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: { deviceId: { exact: nextDevice.deviceId } },
      });

      const newVideoTrack = newStream.getVideoTracks()[0];
      if (!newVideoTrack) return;

      // Replace in peer connection
      if (pcRef.current) {
        const sender = pcRef.current.getSenders().find((s) => s.track?.kind === 'video');
        if (sender) await sender.replaceTrack(newVideoTrack);
      }

      // Swap in local stream
      localStreamRef.current.getVideoTracks().forEach((t) => localStreamRef.current!.removeTrack(t));
      localStreamRef.current.addTrack(newVideoTrack);

      if (localVideoRef.current) localVideoRef.current.srcObject = localStreamRef.current;

      setCurrentVideoDeviceIdx(nextIdx);
    } catch (err) {
      console.error('[ActiveCallUI] Camera flip error:', err);
    }
  }, [videoDevices, currentVideoDeviceIdx]);

  const handleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const handleEndCall = useCallback(async () => {
    if (!user?.uid) return;
    if (durationTimerRef.current) clearInterval(durationTimerRef.current);
    micVuCleanupRef.current?.();
    micVuCleanupRef.current = null;
    if (localStreamRef.current) stopMediaStream(localStreamRef.current);
    if (remoteStreamRef.current) stopMediaStream(remoteStreamRef.current);
    if (pcRef.current) closePeerConnection(pcRef.current);
    try {
      await endCall(callId, user.uid);
    } catch (err) {
      console.error('[ActiveCallUI] End call error:', err);
    }
    onCallEnded?.();
  }, [callId, user?.uid, onCallEnded]);

  // ── Don't render until we have the call doc ────────────────────────────────
  if (!call || call.status !== 'active') return null;

  const isVideoCall = call.type === 'video';
  const isReceiver = call.callerId !== user?.uid;
  const remoteName = isReceiver ? call.callerName : call.receiverName;
  const remoteAvatar = isReceiver ? call.callerAvatar : call.receiverAvatar;
  const remoteInitial = remoteName?.[0]?.toUpperCase() ?? '?';

  // ════════════════════════════════════════════════════════════════════════════
  // ── AUDIO CALL UI ─────────────────────────────────────────────────────────
  // ════════════════════════════════════════════════════════════════════════════
  if (!isVideoCall) {
    return (
      <div
        ref={containerRef}
        className="fixed inset-0 z-[200] flex flex-col items-center justify-between bg-gradient-to-b from-gray-900 via-gray-900 to-black overflow-hidden"
      >
        {/* Hidden audio element for remote audio output */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio ref={remoteAudioRef} autoPlay playsInline className="hidden" />
        {/* Top: caller info */}
        <div className="flex flex-col items-center gap-5 pt-20 px-6 flex-1 justify-center">
          {/* Animated rings */}
          <div className="relative flex items-center justify-center">
            <span className="absolute h-40 w-40 rounded-full bg-green-500/10 animate-ping" style={{ animationDuration: '2s' }} />
            <span className="absolute h-32 w-32 rounded-full bg-green-500/15 animate-ping" style={{ animationDuration: '2s', animationDelay: '0.4s' }} />
            <Avatar className="h-28 w-28 ring-4 ring-white/20 ring-offset-4 ring-offset-gray-900 shadow-2xl z-10">
              <AvatarImage src={remoteAvatar} alt={remoteName} />
              <AvatarFallback className="text-4xl bg-gray-700 text-white" name={remoteName}>{remoteInitial}</AvatarFallback>
            </Avatar>
          </div>

          {/* Name + status */}
          <div className="text-center space-y-1.5">
            <h2 className="text-3xl font-bold text-white tracking-tight">{remoteName}</h2>
            <p className="text-base text-white/50 font-medium">
              {webRTCReady ? formatDuration(callDuration) : 'Connecting…'}
            </p>
          </div>
        </div>

        {/* Bottom: controls */}
        <div className="pb-16 px-8 w-full max-w-sm mx-auto">
          <div className="flex items-end justify-around">
            {/* Mute */}
            <ControlBtn onClick={handleMuteToggle} active={isMuted} label={isMuted ? 'Unmute' : 'Mute'}>
              {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
            </ControlBtn>

            {/* End call */}
            <ControlBtn onClick={handleEndCall} danger large label="End call">
              <PhoneOff className="h-7 w-7" />
            </ControlBtn>

            {/* Speaker */}
            <ControlBtn onClick={handleSpeakerToggle} active={!isSpeakerOn} label={isSpeakerOn ? 'Speaker off' : 'Speaker on'}>
              {isSpeakerOn ? <Volume2 className="h-6 w-6" /> : <VolumeX className="h-6 w-6" />}
            </ControlBtn>
          </div>
        </div>
      </div>
    );
  }

  // ════════════════════════════════════════════════════════════════════════════
  // ── VIDEO CALL UI ─────────────────────────────────────────────────────────
  // ════════════════════════════════════════════════════════════════════════════
  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[200] flex flex-col bg-black overflow-hidden"
    >
      {/* Remote video — fills screen */}
      <video
        ref={remoteVideoRef}
        autoPlay
        playsInline
        className="absolute inset-0 w-full h-full object-cover"
      />

      {/* Placeholder when no remote video yet */}
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-gray-900/80 z-0">
        <Avatar className="h-24 w-24">
          <AvatarImage src={remoteAvatar} alt={remoteName} />
          <AvatarFallback className="text-3xl bg-gray-700 text-white" name={remoteName}>{remoteInitial}</AvatarFallback>
        </Avatar>
        <p className="text-white/60 text-sm">Waiting for video…</p>
      </div>

      {/* Local video — PiP in top-right corner */}
      <div className="absolute top-4 right-4 z-10 w-28 rounded-xl overflow-hidden border-2 border-white/20 shadow-lg aspect-[3/4] bg-gray-800">
        {isCameraOn ? (
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className="w-full h-full object-cover"
            style={{ transform: 'scaleX(-1)' }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-800">
            <VideoOff className="h-6 w-6 text-white/40" />
          </div>
        )}
      </div>

      {/* Top overlay: name + timer */}
      <div className="absolute top-0 left-0 right-0 z-20 bg-gradient-to-b from-black/70 to-transparent pt-safe-top px-4 pt-4 pb-8">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white font-semibold text-lg">{remoteName}</p>
            <p className="text-white/60 text-sm">
              {webRTCReady ? formatDuration(callDuration) : 'Connecting…'}
            </p>
          </div>
          {/* Fullscreen toggle */}
          <button
            onClick={handleFullscreen}
            className="h-9 w-9 flex items-center justify-center rounded-full bg-black/40 text-white/70 hover:text-white"
            aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {/* Bottom overlay: controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-black/80 to-transparent pb-safe-bottom px-8 pb-10 pt-16">
        <div className="flex items-end justify-around max-w-sm mx-auto">
          {/* Mute */}
          <ControlBtn onClick={handleMuteToggle} active={isMuted} label={isMuted ? 'Unmute' : 'Mute'}>
            {isMuted ? <MicOff className="h-6 w-6" /> : <Mic className="h-6 w-6" />}
          </ControlBtn>

          {/* Camera toggle */}
          <ControlBtn onClick={handleCameraToggle} active={!isCameraOn} label={isCameraOn ? 'Camera off' : 'Camera on'}>
            {isCameraOn ? <Video className="h-6 w-6" /> : <VideoOff className="h-6 w-6" />}
          </ControlBtn>

          {/* End call */}
          <ControlBtn onClick={handleEndCall} danger large label="End call">
            <PhoneOff className="h-7 w-7" />
          </ControlBtn>

          {/* Flip camera (only if multiple cameras) */}
          <ControlBtn
            onClick={handleFlipCamera}
            label="Flip camera"
            disabled={videoDevices.length < 2}
          >
            <FlipHorizontal className="h-6 w-6" />
          </ControlBtn>

          {/* Speaker */}
          <ControlBtn onClick={handleSpeakerToggle} active={!isSpeakerOn} label={isSpeakerOn ? 'Speaker off' : 'Speaker on'}>
            {isSpeakerOn ? <Volume2 className="h-6 w-6" /> : <VolumeX className="h-6 w-6" />}
          </ControlBtn>
        </div>
      </div>
    </div>
  );
}
